import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import Copyright from '../components/Copyright';
import theme from '../components/theme';
import { emailRegex, pswRegex } from '../helpers/TextFields';
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Hidden } from '@mui/material';

function Signup() {
  const [openErrorDialog, setOpenErrorDialog] = React.useState(false);
  const [openEmailSentDialog, setOpenEmailSentDialog] = React.useState(false);
  const [circleProg, setCircleProg] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  const handleOpenEmailSentDialog = () => {
    setOpenEmailSentDialog(true);
  };

  const handleCloseEmailSentDialog = () => {
    setOpenEmailSentDialog(false);
  };

  const handleOpenErrorDialog = () => {
    setOpenErrorDialog(true);
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const handleSubmit = (event) => {
    setCircleProg(true);
    event.preventDefault();
    if (!emailRegex.test(email)) {
      console.log("Invalid email.");
      setCircleProg(false);
    } else if (!pswRegex.test(password)) {
      console.log("Invalid password.");
      setCircleProg(false);
    } else {
      const auth = getAuth();
      createUserWithEmailAndPassword(auth, email, password)
        .then((userCredential) => {
          const session_user = userCredential.user;
          console.log("successful sign-up for user: ", session_user);
          sendEmailVerification(session_user)
            .then(() => {
              setCircleProg(false);
              auth.signOut();
              console.log("sent email... ", userCredential.user);
              handleOpenEmailSentDialog();
            })
            .catch((error) => {
              setCircleProg(false);
              console.log(error);
              setErrorCode(error.code);
              handleOpenErrorDialog();
            });
        })
        .catch((error) => {
          setCircleProg(false);
          const errorMessage = error.message;
          setErrorCode(error.code);
          console.log("ERROR on sign-up... code: ", errorCode, "message: ", errorMessage);
          handleOpenErrorDialog();
        });
    }
    setCircleProg(false);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: "75vh",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'analogous.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign up
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  type="email"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleEmailChange}
                  error={!emailRegex.test(email)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={handlePasswordChange}
                  error={!pswRegex.test(password)}
                  helperText="Required: minimum length: 10, at least one: number, lower-case, upper-case, special"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox value="allowExtraEmails" color="primary" />}
                  label="I want to receive inspiration, marketing promotions and updates via email."
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign Up
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link href="./signin" variant="body2">
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
          <Hidden xsUp={!circleProg}>
            <CircularProgress color="success" m={10} />
          </Hidden>
          <Dialog open={openErrorDialog} onClose={handleCloseErrorDialog}>
            <DialogTitle>Sign up failed</DialogTitle>
            <DialogContent>
              <Typography>
                {errorCode}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseErrorDialog}>Close</Button>
            </DialogActions>
          </Dialog>
          <Dialog open={openEmailSentDialog} onClose={handleCloseEmailSentDialog}>
            <DialogTitle>Email Verification Required</DialogTitle>
            <DialogContent>
              <Typography>
                To complete your registration, please verify your email address by clicking the link we sent to <b>{email}</b>. <br /><br />If you do not see the email in your inbox, please check your spam folder.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="contained"
                onClick={() => window.location.href = '/signin'}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
        <Copyright sx={{ mt: 5 }} />
      </Container>
    </ThemeProvider>
  );
}

export default Signup;
